
import type { PeriodT } from '@/core/types/create_order.types'

import { defineComponent, reactive, computed, onBeforeMount } from 'vue'

import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

import useHelpers from '@/common/composable/useHelpers'
import useNotifications from '@/common/composable/useNotifications'

import { DataState } from '@/core/classes/DataState.class'

import { orders } from '@/core/realisations/orders.realisation'

import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

export default defineComponent({
    components: {
        Modal,
        TheInput,
        TheSelect,
        Checkbox,
        FileReader,
        TheButton,
    },
    props: {
        companyId: {
            type: Number,
            default: null,
        },
    },
    emits: ['cancel', 'order-created'],
    setup(props, { emit }) {
        const { findObjectByValue } = useHelpers()
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_period_check: false,
            date: null as string | null,
            period_id: 3,
            period: null as PeriodT | null,
            file: [] as File[],
        })

        const state_form$ = useVuelidate(
            {
                date: { required },
                file: { required },
            },
            state
        )

        const emitCancel = () => {
            if (state.is_loading) return
            emit('cancel')
        }

        const data_state = reactive({
            periods: new DataState<PeriodT>({}),
        })

        const min_date = computed(() => {
            const date = new Date(Date.now())
            const hours = date.getHours()

            if (hours >= 14) {
                date.setDate(date.getDate() + 1)
            }

            return date.toISOString().split('T')[0]
        })

        const onPeriodSelected = (period_id: number) => {
            const period = findObjectByValue('id', period_id, data_state.periods.list)

            if (!period) return
            state.period = period
        }

        const isTodayAfternoon = computed(() => {
            const current_date = new Date(),
                current_hour = current_date.getHours()
            const current_date_iso = current_date.toISOString().split('T')[0]

            return current_hour >= 10 && state.date === current_date_iso
        })

        const setPeriod = () => {
            if (isTodayAfternoon.value) {
                state.period_id = 2
            } else {
                state.period_id = 3
            }

            const period = findObjectByValue('id', state.period_id, data_state.periods.list)
            state.period = period || null
        }

        const onDateSelected = () => {
            state.is_period_check = false
            setPeriod()
        }

        const loadPeriods = async () => {
            data_state.periods.pagination.current_page = 1

            try {
                data_state.periods.is_loading = true
                const response = await orders.getPeriods()
                data_state.periods.list = response.data

                data_state.periods.pagination.total_pages = 1

                data_state.periods.is_failed = false
                data_state.periods.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.periods.failure.catchError(error)

                if (!data_state.periods.failure.error_state.is_cancel) {
                    data_state.periods.is_failed = true
                }
            } finally {
                if (!data_state.periods.failure.error_state.is_cancel) {
                    data_state.periods.is_loading = false
                }
            }
        }

        const getPeriods = computed(() => {
            if (isTodayAfternoon.value) {
                return data_state.periods.list.filter((period) => period.id === 2)
            }

            return data_state.periods.list
        })

        const onCheckboxChange = (value: boolean) => {
            if (!value) {
                setPeriod()
            }
        }

        onBeforeMount(() => {
            loadPeriods()
        })

        const createOrder = async () => {
            if (!props.companyId) {
                notifications.notify({ type: 'danger', title: 'Не выбрана компания' })
                return
            }

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                const form_data = new FormData()
                form_data.append('company_id', props.companyId.toString())
                form_data.append('take_date', state.date as string)

                if (state.period_id) {
                    form_data.append('period_id', state.period_id.toString())
                }

                form_data.append('file', state.file[0])

                await orders.createMultipleOrderFromExcel(form_data)

                notifications.notify({ type: 'success', title: 'Заказ создан' })
                emit('order-created')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При отповом создании заказа произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        return {
            state,
            state_form$,
            data_state,
            min_date,
            emitCancel,
            createOrder,
            onPeriodSelected,
            onDateSelected,
            loadPeriods,
            onCheckboxChange,
            getPeriods,
        }
    },
})
